/**
 * AcceptInvitePage.tsx
 *
 * Landing page for invite links: /accept-invite/:token
 *
 * On mount: validates the token via GET /firms/invite/:token
 * If valid: shows a signup form pre-filled with the email and role
 * If invalid/expired: shows an error with a link back to login
 *
 * App.tsx renders this when window.location.pathname starts with /accept-invite/
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = 'https://coast-terminal-production.up.railway.app';

interface Props {
  onLogin: (token: string, name?: string, role?: string) => void;
}

const PASSWORD_RULES = [
  { test: (p: string) => p.length >= 8,              label: '8+ characters' },
  { test: (p: string) => /[A-Z]/.test(p),            label: 'Uppercase letter' },
  { test: (p: string) => /[a-z]/.test(p),            label: 'Lowercase letter' },
  { test: (p: string) => /[0-9]/.test(p),            label: 'Number' },
  // eslint-disable-next-line no-useless-escape
  { test: (p: string) => /[!@#$%^&*()_\-+=\[\]{};':"\\|,.<>/?`~]/.test(p), label: 'Special character' },
];

const AcceptInvitePage: React.FC<Props> = ({ onLogin }) => {
  const gold = '#C9A96E';
  const bg = '#030A14';
  const surface = '#0A1829';
  const surfaceHigh = '#0F2038';
  const border = '#1A2E44';
  const borderActive = 'rgba(212,175,122,0.30)';
  const textPrimary = '#F0E8D5';
  const textSecondary = '#7A8CA0';
  const textMuted = '#3E5070';
  const red = '#C83030';
  const green = '#1A8A52';
  const serifFont = "'Cormorant Garamond', Georgia, serif";

  const [status, setStatus] = useState<'loading' | 'valid' | 'invalid'>('loading');
  const [accountCreated, setAccountCreated] = useState(false);
  const [firmName, setFirmName] = useState('');
  const [firmColor, setFirmColor] = useState(gold);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('analyst');

  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [pwFocused, setPwFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Extract token from URL path: /accept-invite/{token}
  const token = window.location.pathname.split('/accept-invite/')[1]?.split('/')[0] || '';

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    axios.get(`${API}/firms/invite/${token}`)
      .then(res => {
        setFirmName(res.data.firm_name);
        setFirmColor(res.data.firm_color || gold);
        setInviteEmail(res.data.email);
        setInviteRole(res.data.role);
        setStatus('valid');
      })
      .catch(() => setStatus('invalid'));
  }, [token]); // eslint-disable-line

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    for (const rule of PASSWORD_RULES) {
      if (!rule.test(password)) {
        setError(`Password must include: ${rule.label.toLowerCase()}.`);
        return;
      }
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/firms/invite/${token}/accept`, {
        name: name.trim(),
        username: username.trim().toLowerCase(),
        password,
      });
      // Invite accept no longer issues a JWT — the user must log in, which
      // triggers the first-login email + MFA verification gauntlet.
      setAccountCreated(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Suppress unused-prop warning — onLogin is unused now but kept in the API
  // so App.tsx doesn't need to change its wiring.
  void onLogin;

  const passwordStrength = PASSWORD_RULES.filter(r => r.test(password)).length;
  const strengthColor = passwordStrength <= 2 ? '#C83030' : passwordStrength <= 3 ? '#D4AF7A' : passwordStrength === 4 ? '#3A8BBF' : '#1A8A52';

  const inp = (focused: boolean): React.CSSProperties => ({
    width: '100%', boxSizing: 'border-box', backgroundColor: focused ? '#152540' : surfaceHigh,
    border: `1px solid ${focused ? borderActive : border}`, borderRadius: '3px',
    padding: '12px 14px', color: textPrimary, fontSize: '14px', outline: 'none',
    fontFamily: 'inherit', marginBottom: '16px',
    boxShadow: focused ? '0 0 0 3px rgba(212,175,122,0.08)' : 'none',
    transition: 'border-color 0.2s, background-color 0.2s, box-shadow 0.2s',
  });

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', -apple-system, sans-serif", padding: '40px 20px' }}>
      <div style={{ width: '100%', maxWidth: '440px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '8px', fontFamily: serifFont, background: `linear-gradient(90deg, ${firmColor} 0%, #E8C98A 50%, ${firmColor} 100%)`, backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shimmer 5s linear infinite', display: 'inline-block' }}>
            COAST
          </div>
          {firmName && <div style={{ fontSize: '10px', color: textMuted, letterSpacing: '2px', textTransform: 'uppercase', marginTop: '6px', fontWeight: 700 }}>{firmName}</div>}
        </div>

        {/* Loading */}
        {status === 'loading' && (
          <div style={{ textAlign: 'center', color: textSecondary, fontSize: '14px', fontFamily: serifFont }}>
            Validating your invite...
          </div>
        )}

        {/* Invalid */}
        {status === 'invalid' && (
          <div style={{ backgroundColor: surface, border: `1px solid ${border}`, padding: '32px', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 300, fontFamily: serifFont, color: textPrimary, marginBottom: '12px' }}>Invite Not Found</div>
            <div style={{ fontSize: '13px', color: textSecondary, lineHeight: 1.7, marginBottom: '24px', fontFamily: serifFont }}>
              This invite link is invalid or has expired. Please ask your administrator to send a new one.
            </div>
            <a href="/" style={{ display: 'inline-block', padding: '11px 28px', backgroundColor: gold, color: bg, fontWeight: 800, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', textDecoration: 'none' }}>
              Back to Login
            </a>
          </div>
        )}

        {/* Account created — send them to login to complete email+MFA */}
        {status === 'valid' && accountCreated && (
          <div style={{ backgroundColor: surface, border: `1px solid ${border}`, padding: '32px', textAlign: 'center' }}>
            <div style={{ fontSize: '22px', fontWeight: 300, fontFamily: serifFont, color: textPrimary, marginBottom: '12px' }}>
              Account created.
            </div>
            <div style={{ fontSize: '13px', color: textSecondary, lineHeight: 1.7, marginBottom: '24px', fontFamily: serifFont }}>
              Sign in with your new credentials. We'll email you a 6-digit code
              to confirm your identity, then set up two-factor authentication —
              required for all Coast accounts.
            </div>
            <a href="/" style={{ display: 'inline-block', padding: '12px 32px', backgroundColor: gold, color: bg, fontWeight: 800, fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', textDecoration: 'none' }}>
              Sign In
            </a>
          </div>
        )}

        {/* Valid — show signup form */}
        {status === 'valid' && !accountCreated && (
          <div style={{ backgroundColor: surface, border: `1px solid ${border}`, padding: '32px' }}>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '26px', fontWeight: 300, fontFamily: serifFont, color: textPrimary, marginBottom: '6px' }}>
                You've been invited.
              </div>
              <div style={{ fontSize: '13px', color: textMuted }}>
                Create your account to access {firmName || 'Coast'}.
              </div>
            </div>

            {/* Email badge — pre-filled, read-only */}
            <div style={{ backgroundColor: 'rgba(201,169,110,0.06)', border: `1px solid rgba(201,169,110,0.2)`, padding: '10px 14px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '9px', color: gold, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '3px' }}>Invited as</div>
                <div style={{ fontSize: '13px', color: textPrimary }}>{inviteEmail}</div>
              </div>
              <div style={{ fontSize: '9px', color: gold, letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 700, backgroundColor: 'rgba(201,169,110,0.1)', padding: '3px 8px' }}>
                {inviteRole}
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Full name */}
              <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '7px' }}>Full Name</div>
              <FocusInput value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" type="text" required maxLength={100} inp={inp} />

              {/* Username */}
              <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '7px' }}>Username</div>
              <FocusInput value={username} onChange={e => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 30))} placeholder="your_username" type="text" required inp={inp} />

              {/* Password */}
              <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '7px' }}>Password</div>
              <div style={{ position: 'relative', marginBottom: '8px' }}>
                <FocusInput value={password} onChange={e => setPassword(e.target.value)} onFocus={() => setPwFocused(true)} onBlur={() => setPwFocused(false)} placeholder="••••••••••" type={showPass ? 'text' : 'password'} required maxLength={128} inp={inp} extraStyle={{ paddingRight: '44px', marginBottom: 0 }} />
                <button type="button" onClick={() => setShowPass(v => !v)} style={{ position: 'absolute', right: '14px', top: '14px', background: 'none', border: 'none', cursor: 'pointer', color: textMuted, fontSize: '12px', fontFamily: 'inherit', letterSpacing: '1px', padding: '2px 4px' }}>
                  {showPass ? 'HIDE' : 'SHOW'}
                </button>
              </div>

              {/* Strength bar */}
              <div style={{ marginBottom: '20px', overflow: 'hidden', maxHeight: pwFocused || password.length > 0 ? '100px' : '0', transition: 'max-height 0.3s ease' }}>
                <div style={{ height: '2px', backgroundColor: 'rgba(26,46,68,0.8)', marginBottom: '10px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(passwordStrength / PASSWORD_RULES.length) * 100}%`, backgroundColor: strengthColor, transition: 'width 0.3s, background-color 0.3s' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 16px' }}>
                  {PASSWORD_RULES.map(rule => {
                    const passed = rule.test(password);
                    return (
                      <div key={rule.label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: passed ? green : textMuted }}>
                        <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: passed ? green : '#1A2E44', flexShrink: 0 }} />
                        {rule.label}
                      </div>
                    );
                  })}
                </div>
              </div>

              {error && <div style={{ backgroundColor: 'rgba(200,48,48,0.08)', border: '1px solid rgba(200,48,48,0.22)', borderLeft: '3px solid #C83030', borderRadius: '3px', padding: '11px 14px', marginBottom: '16px', fontSize: '12px', color: '#E07070' }}>{error}</div>}

              <button type="submit" disabled={loading} style={{ width: '100%', padding: '14px', backgroundColor: loading ? 'rgba(15,32,56,0.8)' : gold, color: loading ? textMuted : bg, border: 'none', borderRadius: '3px', fontWeight: 800, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </form>
          </div>
        )}

        <style>{`@keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }`}</style>
      </div>
    </div>
  );
};

// Simple stateful input wrapper to handle focus styling
interface FocusInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  maxLength?: number;
  autoFocus?: boolean;
  inp: (focused: boolean) => React.CSSProperties;
  extraStyle?: React.CSSProperties;
}

function FocusInput({ inp, extraStyle, onFocus, onBlur, ...props }: FocusInputProps) {
  const [focused, setFocused] = React.useState(false);
  return (
    <input
      {...props}
      onFocus={() => { setFocused(true); onFocus?.(); }}
      onBlur={() => { setFocused(false); onBlur?.(); }}
      style={{ ...inp(focused), ...extraStyle }}
    />
  );
}

export default AcceptInvitePage;